import React, { useEffect } from 'react'
import { FormikErrors, useField, useFormikContext } from 'formik'
import styled from 'particles/themes/styled'
import { useTranslation } from 'react-i18next'
import Label from 'particles/Label'
import { FormikTextField } from 'particles/TextInputs/TextField'
import RectangleButton from 'particles/Button/RectangleButton'
import Text from 'particles/Text'
import IconButton from 'particles/Button/IconButton'

interface ContactInformationData {
    full_name: string
    job_title: string
    email: string
    contact: string
}

interface Props {
    showError?: boolean
}

const ContactInformation = ({ showError }: Props) => {
    const [field, meta] = useField<ContactInformationData[]>('contact_information')
    const errors = meta.error as FormikErrors<Record<number, ContactInformationData>>
    const { setFieldValue } = useFormikContext()
    const [t] = useTranslation()

    const canDeleteItem = field.value?.length > 1
    const canAddMore = field.value?.length < 3
    const emailFormatError = t('errorMessage.validation.enterValidEmail', { email: 'name@example.com' })

    useEffect(() => {
        if (!field.value?.length) {
            setFieldValue('contact_information', [
                {
                    contact: '',
                    email: '',
                    full_name: '',
                    job_title: '',
                },
            ])
        }
    }, [field.value])

    const handleDeleteItem = (id: number) => {
        const newValue = field.value.filter((value: ContactInformationData, idx: number) => idx !== id)
        setFieldValue('contact_information', newValue)
    }

    return (
        <ContactWrapper>
            <Label
                title={t('team.billingInformation.businessManagerContactSection.title')}
                subtext={t('team.billingInformation.businessManagerContactSection.subtext')}
            />

            {field.value?.map((item: ContactInformationData, idx: number) => (
                <ContactCard key={idx}>
                    <div className="flex-row">
                        <Text scale="bodyM700">
                            {t('team.billingInformation.businessManager.title', {
                                businessManagerNumber: idx + 1,
                            })}
                        </Text>
                        {canDeleteItem && (
                            <IconButton
                                className="close-button"
                                icon="x"
                                form="icon"
                                color="neutral"
                                onClick={() => handleDeleteItem(idx)}
                            />
                        )}
                    </div>

                    <div className="flex-row">
                        <FormikTextField
                            name={`contact_information.${idx}.full_name`}
                            label={t('accountDetails.personal.nameHeader')}
                            isRequired
                            dataTestId="test-business-contact-information-name"
                        />
                        <FormikTextField
                            name={`contact_information.${idx}.job_title`}
                            label={t('team.billingInformation.businessManager.jobTitle.title')}
                            isRequired
                            dataTestId="test-business-contact-information-title"
                        />
                    </div>
                    <div className="flex-row">
                        <FormikTextField
                            type="email"
                            name={`contact_information.${idx}.email`}
                            label={t('logIn.resetPassword.emailTitle')}
                            isRequired
                            dataTestId="test-business-contact-information-email"
                            showError={showError || errors?.[idx]?.email === emailFormatError}
                            validateByTouch={false}
                        />
                        <FormikTextField
                            name={`contact_information.${idx}.contact`}
                            label={t('team.billingInformation.businessManager.phone.title')}
                            isRequired
                            dataTestId="test-business-contact-information-number"
                        />
                    </div>
                </ContactCard>
            ))}

            {canAddMore && (
                <RectangleButton
                    label={t('team.billingInformation.businessManager.addBusinessManagerButton')}
                    icon="plus"
                    type="button"
                    form="outlined"
                    onClick={() =>
                        setFieldValue('contact_information', [
                            ...field.value,
                            {
                                full_name: '',
                                job_title: '',
                                email: '',
                                contact: '',
                            },
                        ])
                    }
                />
            )}
        </ContactWrapper>
    )
}

export default ContactInformation

const ContactCard = styled.div`
    width: 100%;
    background: ${({ theme }) => theme.colors.surface.primary};
    box-shadow: 0px 0px 12px 0px #0000001f;
    padding: 24px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[600]};
`

const ContactWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[400]};
    align-items: flex-start;
`
